import axios from 'axios';
import { API_BASE_URL, handleApiError } from './_apiBase';

// ── zigbee.db per gateway (nivå 3) ───────────────────────────────────────────

/** Laddar ner samordnarens zigbee.db som fil. */
export const downloadZigbeeDb = async (gatewayId: string): Promise<Blob> => {
    try { return (await axios.get(`${API_BASE_URL}/gateways/${gatewayId}/zigbee-db`, { responseType: 'blob' })).data; }
    catch (e) { return handleApiError(e, 'Ladda ner zigbee.db'); }
};

/** Byter ut zigbee.db; samordnaren startar om på servern. */
export const uploadZigbeeDb = async (gatewayId: string, file: File): Promise<{ message: string }> => {
    try {
        const buf = await file.arrayBuffer();
        const res = await axios.post<{ message: string }>(`${API_BASE_URL}/gateways/${gatewayId}/zigbee-db`, buf, {
            headers: { 'Content-Type': 'application/octet-stream' },
        });
        return res.data;
    } catch (e) { return handleApiError(e, 'Ladda upp zigbee.db'); }
};

/**
 * Fetch LAN ping reports from all gateways that have posted a report.
 */
export type GatewayReportEntry = {
    gatewayId: string;
    gatewayName: string;
    gatewayIp?: string;
    devices: Array<{ name: string; url: string; online: boolean; responseTime: number | null }>;
    reportedAt: string | null;
};

export const fetchGatewayReport = async (): Promise<GatewayReportEntry[]> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/gateway/report`, { timeout: 5000 });
        return Array.isArray(response.data) ? response.data : [];
    } catch {
        return [];
    }
};

// ─── Gateway CRUD ─────────────────────────────────────────────────────────────

export interface CoordinatorStatus {
    connected: boolean;
    state: string;
}

export interface GatewayRecord {
    id: string;
    name: string;
    ip: string;
    description?: string;
    createdAt: string;
    report?: GatewayReportEntry | null;
    coordinatorStatus?: CoordinatorStatus;
    // Zigbee coordinator fields
    zigbeeEnabled?: boolean;
    zigbeeIp?: string;
    zigbeePort?: number;
    zigbeeChannel?: number;
    zigbeePanId?: number;
    zigbeeExtendedPanId?: string;
    zigbeeNetworkKey?: string;
    scriptUrl?: string;
}

export interface GatewayZigbeeFields {
    zigbeeEnabled?: boolean;
    zigbeeIp?: string;
    zigbeePort?: number;
    zigbeeChannel?: number;
    zigbeePanId?: number;
    zigbeeExtendedPanId?: string;
    zigbeeNetworkKey?: string;
}

export const fetchGateways = async (): Promise<GatewayRecord[]> => {
    const response = await axios.get(`${API_BASE_URL}/gateways`);
    return response.data;
};

export const addGateway = async (data: { id: string; name: string; ip: string; description?: string; scriptUrl?: string } & GatewayZigbeeFields): Promise<GatewayRecord> => {
    const response = await axios.post(`${API_BASE_URL}/gateways`, data);
    return response.data;
};

export const updateGateway = async (id: string, data: { name?: string; ip?: string; description?: string; scriptUrl?: string } & GatewayZigbeeFields): Promise<GatewayRecord> => {
    const response = await axios.put(`${API_BASE_URL}/gateways/${id}`, data);
    return response.data;
};

export const testContact = async (id: number, channel: 'email' | 'sms' | 'push'): Promise<{ message: string }> => {
    const response = await axios.post(`${API_BASE_URL}/alert-rules/contacts/${id}/test`, { channel });
    return response.data;
};

export const deleteGateway = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/gateways/${id}`);
};

/**
 * TCP ping the Zigbee gateway directly via the backend.
 * Returns online flag, response time in ms, and the target IP/port.
 */
export const fetchGatewayPing = async (): Promise<{
    online: boolean;
    responseTime: number | null;
    ip: string;
    port: number;
}> => {
    try {
        const response = await axios.get(`${API_BASE_URL}/gateway/ping`, { timeout: 5000 });
        return response.data;
    } catch {
        return { online: false, responseTime: null, ip: '192.168.5.158', port: 6638 };
    }
};

// ─── API Key management ───────────────────────────────────────────────────────

export interface ApiKeyRecord {
    id: number;
    label: string;
    key_type: string;
    gateway_ids: string[];
    device_id: number | null;
    device_name: string | null;
    created_at: string;
    last_used_at: string | null;
}

export const fetchApiKeys = async (): Promise<ApiKeyRecord[]> => {
    const resp = await axios.get(`${API_BASE_URL}/api-keys`);
    // Only show M2M (gateway) keys here; kiosk keys are managed in KioskAdmin
    return (resp.data as ApiKeyRecord[]).filter(k => !k.key_type || k.key_type === 'm2m');
};

export interface CreateApiKeyOptions {
    /** Create a new 'm2m' heartbeat device with this name and link the key to it. */
    deviceName?: string;
    /** Expected heartbeat cadence in seconds — the watchdog marks the device offline once this elapses. */
    heartbeatIntervalSeconds?: number;
}

export const createApiKey = async (
    label: string,
    gatewayIds: string[],
    opts?: CreateApiKeyOptions,
): Promise<{ id: number; key: string; label: string; gatewayIds: string[]; device_id: number | null; created_at: string }> => {
    const resp = await axios.post(`${API_BASE_URL}/api-keys`, {
        label,
        gatewayIds,
        deviceName: opts?.deviceName,
        heartbeatIntervalSeconds: opts?.heartbeatIntervalSeconds,
    });
    return resp.data;
};

export const deleteApiKey = async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/api-keys/${id}`);
};

// ─── Offline detection settings ───────────────────────────────────────────────

export interface OfflineSettings {
    router_timeout_hours: number;
    end_device_timeout_hours: number;
}

export const fetchOfflineSettings = async (): Promise<OfflineSettings> => {
    const resp = await axios.get(`${API_BASE_URL}/alert-rules/offline-settings`);
    return resp.data;
};

export const updateOfflineSettings = async (data: Partial<OfflineSettings>): Promise<void> => {
    await axios.put(`${API_BASE_URL}/alert-rules/offline-settings`, data);
};
